import { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { extractInvoiceData, extractInvoiceDataBase64, extractInvoiceDataMulti, matchToIngredients, normalizeAlias, postProcessExtraction } from '../lib/invoiceApi';
import { fmt$, fmt$0 } from '../utils/formatters';
import { parseLocalDate } from '../utils/dates';
import InvoiceReview from './InvoiceReview';
import InvoiceOriginalViewer from './InvoiceOriginalViewer';
import { createVendor, resolveVendor, assignVendorAlias } from '../lib/vendors';
import { suggestEC, COGS_CATEGORIES } from '../lib/feeDetection';
import { resolveDuplicate } from '../lib/duplicateResolution';
import { cleanInvNumber, normalizeInvNumber, normalizeVendor, vendorsFuzzyMatch } from '../lib/invoiceUtils';
import { sharedConfirmInvoice } from '../lib/confirmInvoice';

function ScanProgress({stage,t}){
  const[elapsed,setElapsed]=useState(0);
  useEffect(()=>{if(stage!=='reading')return;setElapsed(0);const iv=setInterval(()=>setElapsed(s=>s+1),1000);return()=>clearInterval(iv);},[stage]);
  return <div style={{textAlign:"center",padding:"40px 0"}}>
    <div style={{fontSize:24,marginBottom:12}}>{stage==='uploading'?'📤':'🔍'}</div>
    <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>{stage==='uploading'?'Uploading...':'Reading invoice...'}</div>
    <div style={{fontSize:11,color:t.td}}>{stage==='uploading'?'Sending to AI':'Extracting line items, prices, and totals'}</div>
    {stage==='reading'&&elapsed>2&&<div style={{fontSize:11,color:t.td,marginTop:8}}>{elapsed}s</div>}
  </div>;
}

const LS_KEY='mise_invoice_scan';
// Cache org ingredient categories (name → id) for auto-created ingredients
const ingCatsCache={};
async function loadIngCats(supabase,orgId){
  if(ingCatsCache[orgId])return ingCatsCache[orgId];
  const{data}=await supabase.from('ingredient_categories').select('id,name').eq('org_id',orgId);
  const m=new Map((data||[]).map(r=>[r.name,r.id]));
  ingCatsCache[orgId]=m;
  return m;
}
function loadSaved(){try{const s=localStorage.getItem(LS_KEY);return s?JSON.parse(s):null;}catch(e){return null;}}
function saveScan(data){try{localStorage.setItem(LS_KEY,JSON.stringify(data));}catch(e){}}
function clearScan(){try{localStorage.removeItem(LS_KEY);}catch(e){}}


export default function InvoiceScanner({t,si,sb,sh,sd,mn,orgId,uid,ings,setIngs,onConfirm,settings,save,salesDate,setOtherExp,refreshBell,mi,batches,bc,setPg,setDuplicateData,onScanComplete,onClose}){
async function loadAliasMap(){
  if(!orgId)return new Map();
  const{data}=await supabase.from('ingredient_aliases').select('normalized_alias,ingredient_id').eq('org_id',orgId);
  return new Map((data||[]).map(r=>[r.normalized_alias,r.ingredient_id]));
}
// Restore persisted state on mount
const saved=useRef(loadSaved());
const[stage,setStage]=useState(()=>saved.current?.stage||'upload');
const[error,setError]=useState('');
const[extracted,setExtracted]=useState(()=>saved.current?.extracted||null);
const[matched,setMatched]=useState(()=>saved.current?.matched||[]);
const[duplicate,setDuplicate]=useState(()=>saved.current?.duplicate||null);
const[duplicateOnConfirm,setDuplicateOnConfirm]=useState(null);
const[pendingSupersede,setPendingSupersede]=useState(()=>saved.current?.pendingSupersede||null);
const[priceUpdates,setPriceUpdates]=useState(()=>saved.current?.priceUpdates||{});
const[dragging,setDragging]=useState(false);
const[newItemEC,setNewItemEC]=useState(()=>saved.current?.newItemEC||{});
const[newItemCat,setNewItemCat]=useState({});
const[uploadedFile,setUploadedFile]=useState(null); // {base64, type, ext}
const[invoicePages,setInvoicePages]=useState(()=>saved.current?.invoicePages||[]); // [{storagePath, mediaType}] — pages in current review
const[previewUnavailable,setPreviewUnavailable]=useState(false);
const[queue,setQueue]=useState([]); // bulk queue items
const[queueIdx,setQueueIdx]=useState(0); // current review index in queue
const[scanProgress,setScanProgress]=useState(null); // {current,total,fileName} during bulk scanning
const[queueLoading,setQueueLoading]=useState(false); // true while activateQueueItem runs matching
const[confirming,setConfirming]=useState(false); // true while confirmAndAdvance runs
const queueRef=useRef([]); // ref mirror of queue for use in closures
const queueIdxRef=useRef(0); // ref mirror of queueIdx for use in closures
function setQueueIdxSync(v){queueIdxRef.current=v;setQueueIdx(v);}
const ingsRef=useRef(ings); // ref mirror of ings for use in async closures
useEffect(()=>{ingsRef.current=ings;},[ings]);
const addPageRef=useRef();
const fileRef=useRef();const cameraRef=useRef();const dragCounter=useRef(0);

// Persist scan state to localStorage whenever review data changes
useEffect(()=>{
  if(stage==='review'&&extracted){
    saveScan({stage,extracted,matched,duplicate,priceUpdates,newItemEC,invoicePages,pendingSupersede});
  }else if(stage==='upload'||stage==='done'){
    clearScan();setInvoicePages([]);setPreviewUnavailable(false);
  }
},[stage,extracted,matched,duplicate,priceUpdates,newItemEC,invoicePages,pendingSupersede]);

// Normalize invoice date: fix MM/DD/YY → YYYY-MM-DD, flag if >60 days from now
function normalizeInvoiceDate(raw){
  if(!raw)return{date:null,needsReview:'no_date'};
  let d=raw.trim();
  // Handle MM/DD/YY or MM/DD/YYYY
  const slashMatch=d.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if(slashMatch){
    let[,mm,dd,yy]=slashMatch;
    if(yy.length===2){const cent=parseInt(yy)>50?'19':'20';yy=cent+yy;}
    d=`${yy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;
  }
  if(!/^\d{4}-\d{2}-\d{2}$/.test(d))return{date:null,needsReview:'no_date'};
  const parsed=new Date(d+'T12:00:00');
  if(isNaN(parsed))return{date:null,needsReview:'no_date'};
  const today=new Date();today.setHours(12,0,0,0);
  const year=parsed.getFullYear();
  const daysFromToday=(parsed-today)/(1000*60*60*24);
  if(daysFromToday>1)return{date:d,needsReview:'future'};
  if(year<2020||daysFromToday>365)return{date:d,needsReview:'bad_year'};
  return{date:d,needsReview:false};
}

// Find closest existing vendor match (>90% similar by character overlap)
function matchExistingVendor(normalized,existingVendors){
  if(!normalized||!existingVendors?.length)return normalized;
  const nLow=normalized.toLowerCase();
  for(const v of existingVendors){
    const vLow=v.toLowerCase();
    if(vLow===nLow)return v; // exact match (case-insensitive)
    // Simple similarity: shared chars / max length
    const maxLen=Math.max(nLow.length,vLow.length);
    if(maxLen===0)continue;
    let matches=0;const shorter=nLow.length<=vLow.length?nLow:vLow;const longer=nLow.length>vLow.length?nLow:vLow;
    for(let i=0;i<shorter.length;i++){if(shorter[i]===longer[i])matches++;}
    if(matches/maxLen>=0.9)return v; // use existing spelling
  }
  return normalized;
}

// Build category hints string from org's categories + example ingredients
function buildCategoryHints(orgCats,ings){
  if(!orgCats?.size)return null;
  const byId={};
  for(const ig of(ings||[])){if(ig.categoryId&&ig.active!==false){if(!byId[ig.categoryId])byId[ig.categoryId]=[ig.n];else if(byId[ig.categoryId].length<5)byId[ig.categoryId].push(ig.n);}}
  const parts=[];
  for(const[name,id]of orgCats){const examples=byId[id];if(examples?.length)parts.push(`${name} (e.g. ${examples.join(', ')})`);else parts.push(name);}
  return'classify into exactly one of: '+parts.join(', ')+'. Use "Other" if nothing fits.';
}

async function handleFile(file){
  setError('');setDuplicate(null);
  if(!file)return;
  if(file.size>20*1024*1024)return setError('File too large (max 20MB)');
  const validTypes=['image/jpeg','image/png','image/webp','image/gif','image/heic','image/heif','application/pdf'];
  if(!validTypes.includes(file.type)&&!file.name.match(/\.heic$/i))return setError('Unsupported file type. Use JPG, PNG, HEIC, or PDF.');

  setStage('uploading');
  try{
    const mimeType=file.type||'image/jpeg';
    const ext=file.name.split('.').pop().toLowerCase()||'jpg';
    const base64=await new Promise((res,rej)=>{const r2=new FileReader();r2.onload=()=>res(r2.result.split(',')[1]);r2.onerror=rej;r2.readAsDataURL(file);});
    setUploadedFile({base64,type:mimeType,ext});

    // Upload to temp storage for preview (non-blocking)
    const tempPath=`${orgId}/tmp/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
    setPreviewUnavailable(false);
    supabase.storage.from('invoices').upload(tempPath,file,{contentType:mimeType,upsert:true}).then(({data:upData,error:upErr})=>{if(!upErr)setInvoicePages([{storagePath:tempPath,mediaType:mimeType}]);else{console.error('[InvoiceScanner] Temp upload failed:',upErr.message);setPreviewUnavailable(true);}});

    setStage('reading');
    const catHints=buildCategoryHints(await loadIngCats(supabase,orgId),ings);
    const ocrStart=Date.now();
    const data=await extractInvoiceDataBase64(base64,mimeType,catHints);
    data._extractionMs=Date.now()-ocrStart;
    if(!data._docSource)data._docSource=mimeType==='application/pdf'?'pdf_image':'photo';
    const{date:normDate,needsReview:dateNeedsReview}=normalizeInvoiceDate(data.invoice_date);
    data.invoice_date=normDate;
    data.date_needs_review=dateNeedsReview;
    data.line_items=postProcessExtraction(data.line_items);
    const aliasMap=await loadAliasMap();
    const items=matchToIngredients(data.line_items||[],ings,data.vendor,aliasMap);

    // Vendor resolution: check vendors table + aliases first, then fall back to invoice-name matching
    let vendor=normalizeVendor(data.vendor);
    if(orgId){
      const resolved=await resolveVendor(orgId,vendor);
      if(resolved)vendor=resolved.name;
      else{
        const{data:ev}=await supabase.from('invoices').select('vendor').eq('org_id',orgId);
        if(ev?.length){const distinct=[...new Set(ev.map(r=>r.vendor))];vendor=matchExistingVendor(vendor,distinct);}
      }
    }
    let invNumber=cleanInvNumber(data.invoice_number);
    if(!invNumber){
      const d2=(data.invoice_date||new Date().toISOString().split('T')[0]).replace(/-/g,'');
      const vs=(data.vendor||'UNKNOWN').toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,12);
      const now2=new Date();invNumber=`RECEIPT-${d2}-${vs}-${String(now2.getHours()).padStart(2,'0')}${String(now2.getMinutes()).padStart(2,'0')}`;
    }
    if(orgId&&invNumber){
      const norm=normalizeInvNumber(invNumber);
      const{data:existing}=await supabase.from('invoices').select('id,vendor,grand_total,invoice_number,invoice_date,status,doc_quality,doc_quality_confidence,doc_timestamp,created_at').eq('org_id',orgId).is('superseded_by',null);
      const numMatches=(existing||[]).filter(r=>normalizeInvNumber(r.invoice_number)===norm);
      // Fuzzy vendor match, or same number + date + total regardless of vendor
      const dupMatch=numMatches.filter(r=>{
        if(vendorsFuzzyMatch(r.vendor,vendor))return true;
        if(r.invoice_date===(data.invoice_date||null)&&Math.abs(Number(r.grand_total||0)-Number(data.grand_total||0))<0.01)return true;
        return false;
      });
      if(dupMatch?.length){
        const existingIsConfirmed=dupMatch[0].status==='confirmed';
        const resolution=existingIsConfirmed?{action:'manual_review',reason:'Duplicate of a confirmed invoice',keepNew:null}:resolveDuplicate(dupMatch[0],data);
        if(resolution.action==='auto_swap'){
          const totalDiff=Math.abs(Number(data.grand_total||0)-Number(dupMatch[0].grand_total||0));
          if(!resolution.keepNew){
            // Existing wins — tell the user
            setError(`Duplicate: invoice #${invNumber} from ${vendor} already exists ($${Number(dupMatch[0].grand_total||0).toFixed(2)}). This scan was not saved.`);
            setStage('upload');return;
          } else {
            // New wins — defer supersede until new invoice is confirmed
            setPendingSupersede({id:dupMatch[0].id,reason:resolution.reason,totalDiff,vendor,invNumber});
            // Fall through to normal review/confirm flow
          }
        } else {
          // Manual review
          if(setDuplicateData&&setPg){
            setDuplicateData({extracted:data,matched:items,vendor,invNumber,existingInvoiceId:dupMatch[0].id,existingTotal:dupMatch[0].grand_total,uploadedFile:{base64,type:mimeType,ext}});
            setPg('duplicate-review');return;
          }
        }
      }
    }

    const finalExtracted={...data,vendor,invoice_number:invNumber,raw_vendor:data.vendor};
    if(onScanComplete){
      // Expense flow: write staging and hand off immediately
      const stagingId=crypto.randomUUID();
      const storagePath=`${orgId}/tmp_${stagingId}.${file.name.split('.').pop()||'jpg'}`;
      await supabase.storage.from('invoices').upload(storagePath,file,{contentType:mimeType,upsert:true});
      await supabase.from('invoice_staging').insert({id:stagingId,org_id:orgId,vendor:finalExtracted.vendor,invoice_number:finalExtracted.invoice_number,invoice_date:finalExtracted.invoice_date||null,grand_total:finalExtracted.grand_total||0,extracted_data:finalExtracted,matched_items:items,storage_path:storagePath});
      onScanComplete(stagingId);
      return;
    }
    setExtracted(finalExtracted);
    setMatched(items);
    const updates={};items.filter(i=>i.price_changed).forEach(i=>{
      if((i.amount===0||!i.amount)&&(i.qty_shipped||0)>0&&(i.unit_price||0)>0)return;
      const risky=i.cross_distributor||Math.abs(i.pct_change||0)>50;
      updates[i.matched_ingredient_id]=!risky;
    });
    setPriceUpdates(updates);
    setStage('review');
  }catch(e){
    console.error('Invoice extraction error:',e);
    setError(e.message||'Failed to process invoice');
    setStage('upload');
  }
}

// === BULK QUEUE FUNCTIONS ===

async function handleBulkFiles(files){
  const fileList=Array.from(files).filter(f=>{
    if(f.size>20*1024*1024)return false;
    const validTypes=['image/jpeg','image/png','image/webp','image/gif','image/heic','image/heif','application/pdf'];
    return validTypes.includes(f.type)||f.name.match(/\.heic$/i);
  });
  if(!fileList.length)return;

  setStage('scanning');
  const items=new Array(fileList.length).fill(null);
  let completed=0;
  const catHints=buildCategoryHints(await loadIngCats(supabase,orgId),ingsRef.current);

  // Scan a single file and store result at its index
  async function scanOne(file,idx){
    try{
      const mimeType=file.type||'image/jpeg';
      const ext=file.name.split('.').pop().toLowerCase()||'jpg';
      const base64=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(',')[1]);r.onerror=rej;r.readAsDataURL(file);});

      const tempPath=`${orgId}/tmp/${Date.now()}_${idx}-${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      await supabase.storage.from('invoices').upload(tempPath,file,{contentType:mimeType,upsert:true});

      const data=await extractInvoiceDataBase64(base64,mimeType,catHints);
      const{date:normDate,needsReview:dateNeedsReview}=normalizeInvoiceDate(data.invoice_date);
      data.invoice_date=normDate;
      data.date_needs_review=dateNeedsReview;

      let vendor=normalizeVendor(data.vendor);
      if(orgId){
        const resolved=await resolveVendor(orgId,vendor);
        if(resolved)vendor=resolved.name;
        else{
          const{data:ev}=await supabase.from('invoices').select('vendor').eq('org_id',orgId);
          if(ev?.length){const distinct=[...new Set(ev.map(r=>r.vendor))];vendor=matchExistingVendor(vendor,distinct);}
        }
      }
      let invNumber=cleanInvNumber(data.invoice_number);
      if(!invNumber){
        const d2=(data.invoice_date||new Date().toISOString().split('T')[0]).replace(/-/g,'');
        const vs=(data.vendor||'UNKNOWN').toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,12);
        const now2=new Date();invNumber=`RECEIPT-${d2}-${vs}-${String(now2.getHours()).padStart(2,'0')}${String(now2.getMinutes()).padStart(2,'0')}`;
      }

      let dup=null;
      if(orgId&&invNumber){
        const norm=normalizeInvNumber(invNumber);
        const{data:existing}=await supabase.from('invoices').select('id,grand_total,invoice_number').eq('org_id',orgId).eq('vendor',vendor).is('superseded_by',null);
        const dupMatch=existing?.filter(r=>normalizeInvNumber(r.invoice_number)===norm);
        if(dupMatch?.length)dup=dupMatch[0];
      }

      const finalExtracted={...data,vendor,invoice_number:invNumber,raw_vendor:data.vendor};

      items[idx]={
        id:`q_${Date.now()}_${idx}`,
        fileName:file.name,
        status:'ready',
        extracted:finalExtracted,
        uploadedFile:{base64,type:mimeType,ext},
        invoicePages:[{storagePath:tempPath,mediaType:mimeType}],
        matched:null,priceUpdates:{},newItemEC:{},
        duplicate:dup,error:null,
      };
    }catch(e){
      console.error(`[BulkScan] Failed: ${file.name}`,e);
      items[idx]={
        id:`q_${Date.now()}_${idx}`,
        fileName:file.name,
        status:'error',
        extracted:null,uploadedFile:null,invoicePages:[],
        matched:null,priceUpdates:{},newItemEC:{},
        duplicate:null,error:e.message||'Scan failed',
      };
    }
    completed++;
    setScanProgress({current:completed,total:fileList.length,fileName:file.name});
  }

  // Run up to 3 scans concurrently
  const CONCURRENCY=3;
  for(let start=0;start<fileList.length;start+=CONCURRENCY){
    const batch=fileList.slice(start,start+CONCURRENCY);
    await Promise.all(batch.map((file,i)=>scanOne(file,start+i)));
  }

  // Sort by invoice date ascending so oldest confirms first (correct price ordering)
  items.sort((a,b)=>{
    const da=a.extracted?.invoice_date||'9999';
    const db=b.extracted?.invoice_date||'9999';
    return da<db?-1:da>db?1:0;
  });

  setQueue(items);
  queueRef.current=items;
  setScanProgress(null);
  // Start reviewing the first ready item
  const firstReady=items.findIndex(q=>q.status==='ready');
  if(firstReady>=0){
    setQueueIdxSync(firstReady);
    activateQueueItem(items,firstReady);
    setStage('queue');
  }else{
    setStage('queue-done');
  }
}

async function activateQueueItem(q,idx){
  const item=q[idx];
  if(!item||item.status==='error')return;

  // Clear previous invoice state immediately so stale data is never visible
  setQueueLoading(true);
  setExtracted(null);
  setMatched([]);
  setPriceUpdates({});
  setNewItemEC({});
  setInvoicePages(item.invoicePages); // set pages early so viewer starts loading
  setUploadedFile(item.uploadedFile);
  setDuplicate(null);
  setDuplicateOnConfirm(null);
  setError('');

  // Run matching NOW against current ings (via ref, not closure — includes auto-created from earlier confirms)
  const aliasMap=await loadAliasMap();
  item.extracted.line_items=postProcessExtraction(item.extracted.line_items);
  const items=matchToIngredients(item.extracted.line_items||[],ingsRef.current,item.extracted.vendor,aliasMap);

  const updates={};
  items.filter(i=>i.price_changed).forEach(i=>{
    if((i.amount===0||!i.amount)&&(i.qty_shipped||0)>0&&(i.unit_price||0)>0)return;
    const risky=i.cross_distributor||Math.abs(i.pct_change||0)>50;
    updates[i.matched_ingredient_id]=!risky;
  });

  const updated=[...q];
  updated[idx]={...updated[idx],matched:items,priceUpdates:updates,newItemEC:{}};
  setQueue(updated);
  queueRef.current=updated;

  // Set final state for InvoiceReview (will remount via key change)
  setExtracted(item.extracted);
  setMatched(items);
  setPriceUpdates(updates);
  setQueueLoading(false);
}

async function confirmAndAdvance(reconcileInfo){
  if(confirming)return; // prevent double-click
  setConfirming(true);
  try{
    await confirmInvoice(reconcileInfo);
    const ci=queueIdxRef.current;
    setQueue(prev=>{
      const updated=[...prev];
      updated[ci]={...updated[ci],status:'confirmed'};
      queueRef.current=updated;
      return updated;
    });
    advanceQueue();
  }finally{setConfirming(false);}
}

function skipQueueItem(){
  const ci=queueIdxRef.current;
  setQueue(prev=>{
    const updated=[...prev];
    updated[ci]={...updated[ci],status:'skipped'};
    queueRef.current=updated;
    return updated;
  });
  advanceQueue();
}

function advanceQueue(){
  const q=queueRef.current;
  const ci=queueIdxRef.current;
  // Find next ready or skipped item after current
  const next=q.findIndex((item,i)=>i>ci&&(item.status==='ready'||item.status==='skipped'));
  if(next>=0){
    setQueueIdxSync(next);
    activateQueueItem(q,next);
    setStage('queue');
  }else{
    // Check if any skipped items remain (wrap around)
    const skipped=q.findIndex(item=>item.status==='skipped');
    if(skipped>=0){
      setQueueIdxSync(skipped);
      activateQueueItem(q,skipped);
      setStage('queue');
    }else{
      setStage('queue-done');
    }
  }
}

function resetQueue(){
  setQueue([]);
  queueRef.current=[];
  setQueueIdxSync(0);
  setScanProgress(null);
  reset();
}

// === END BULK QUEUE FUNCTIONS ===

async function addPageToReview(file){
  if(!file)return;
  if(file.size>20*1024*1024)return setError('File too large (max 20MB)');
  if(invoicePages.length>=10)return setError('Maximum 10 pages per invoice');
  const validTypes=['image/jpeg','image/png','image/webp','image/gif','image/heic','image/heif','application/pdf'];
  if(!validTypes.includes(file.type)&&!file.name.match(/\.heic$/i))return setError('Unsupported file type.');
  setError('');setStage('reading');
  try{
    const mimeType=file.type||'image/jpeg';
    const tempPath=`${orgId}/tmp/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
    const{error:upErr}=await supabase.storage.from('invoices').upload(tempPath,file,{contentType:mimeType,upsert:true});
    if(upErr)throw new Error(`Upload failed: ${upErr.message}`);

    const newPages=[...invoicePages,{storagePath:tempPath,mediaType:mimeType}];
    setInvoicePages(newPages);

    // Re-OCR all pages combined
    const catHintsRescan=buildCategoryHints(await loadIngCats(supabase,orgId),ingsRef.current);
    const data=newPages.length===1
      ?await extractInvoiceData(newPages[0].storagePath,newPages[0].mediaType,catHintsRescan)
      :await extractInvoiceDataMulti(newPages,catHintsRescan);
    const{date:normDate2,needsReview:dateNeedsReview2}=normalizeInvoiceDate(data.invoice_date);
    data.invoice_date=normDate2;
    data.date_needs_review=dateNeedsReview2;
    data.line_items=postProcessExtraction(data.line_items);
    const aliasMap2=await loadAliasMap();
    const items=matchToIngredients(data.line_items||[],ings,data.vendor,aliasMap2);

    let vendor=normalizeVendor(data.vendor);
    if(orgId){const resolved=await resolveVendor(orgId,vendor);if(resolved)vendor=resolved.name;else{const{data:ev}=await supabase.from('invoices').select('vendor').eq('org_id',orgId);if(ev?.length){const distinct=[...new Set(ev.map(r=>r.vendor))];vendor=matchExistingVendor(vendor,distinct);}}}
    let invNumber=data.invoice_number;
    if(!invNumber){const d2=(data.invoice_date||new Date().toISOString().split('T')[0]).replace(/-/g,'');const vs=(data.vendor||'UNKNOWN').toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,12);const now2=new Date();invNumber=`RECEIPT-${d2}-${vs}-${String(now2.getHours()).padStart(2,'0')}${String(now2.getMinutes()).padStart(2,'0')}`;}

    setExtracted({...data,vendor,invoice_number:invNumber});
    setMatched(items);
    const updates={};items.filter(i=>i.price_changed).forEach(i=>{if((i.amount===0||!i.amount)&&(i.qty_shipped||0)>0&&(i.unit_price||0)>0)return;const risky=i.cross_distributor||Math.abs(i.pct_change||0)>50;updates[i.matched_ingredient_id]=!risky;});
    setPriceUpdates(updates);
    setStage('review');
  }catch(e){
    console.error('Add page error:',e);
    setError(e.message||'Failed to add page');
    setStage('review');
  }
}

async function confirmInvoice(reconcileInfo){
  if(!extracted)return;
  const invDate=extracted.invoice_date||new Date().toISOString().split('T')[0];
  const grandTotal=reconcileInfo?.editedTotal??(extracted.grand_total||(extracted.subtotal||0)+(extracted.bottle_deposit||0)+(extracted.freight||0)+(extracted.fuel_surcharge||0)+(extracted.other_fees||0));
  const vendor=extracted.vendor;
  const invNumber=cleanInvNumber(extracted.invoice_number);

  const result=await sharedConfirmInvoice({
    orgId,uid,extracted,matched,vendor,invNumber,invDate,grandTotal,
    confirmPath:'scanner',reconcileInfo,priceUpdates,newItemEC,newItemCat,
    uploadedFile,invoicePages,
    ings,setIngs,ingsRef,setOtherExp,
    settings,mi,batches,bc,save,
    onConfirm,refreshBell,
    pendingSupersede,setPendingSupersede,
    queueRef,queueIdxRef,
    skipDupCheck:reconcileInfo?.skipDupCheck,
  });

  if(result?.blocked==='duplicate'){setDuplicateOnConfirm(result.duplicate);return;}
  if(result?.error){setError(result.error);return;}
  setStage('done');
}

// Old confirmInvoice body removed — now uses sharedConfirmInvoice above.
// replaceExistingInvoice remains as a Scanner-specific wrapper.

const[deferMsg,setDeferMsg]=useState('');

async function deferToTasks(){
  if(!extracted||!orgId)return;
  setError('');
  try{
    // Re-upload file to tmp storage for later retrieval, or reuse existing temp path
    let storagePath=null;
    if(uploadedFile){
      storagePath=`${orgId}/tmp/${Date.now()}-deferred.${uploadedFile.ext}`;
      const fileBytes=Uint8Array.from(atob(uploadedFile.base64),c=>c.charCodeAt(0));
      await supabase.storage.from('invoices').upload(storagePath,fileBytes,{contentType:uploadedFile.type,upsert:true});
    }else if(invoicePages.length>0&&invoicePages[0].storagePath){
      storagePath=invoicePages[0].storagePath;
    }

    // Insert staging row with current (possibly edited) matched items
    const stagingId=crypto.randomUUID();
    const{error:stgErr}=await supabase.from('invoice_staging').insert({
      id:stagingId,org_id:orgId,
      vendor:extracted.vendor,
      invoice_number:extracted.invoice_number,
      invoice_date:extracted.invoice_date||null,
      grand_total:extracted.grand_total||0,
      extracted_data:extracted,
      matched_items:matched,
      storage_path:storagePath
    });
    if(stgErr)throw new Error(stgErr.message);

    // Create task
    const{data:task,error:tkErr}=await supabase.from('tasks').insert({
      org_id:orgId,type:'invoice_review',status:'pending',
      title:`Review: ${extracted.vendor} $${(extracted.grand_total||0).toFixed(2)}`,
      note:extracted.invoice_number?`Invoice #${extracted.invoice_number}`:`${(matched||[]).length} line items`,
      source:'manual',priority:5,
      target_page:'invoice-review',target_id:stagingId,
      payload:{staging_id:stagingId}
    }).select().single();
    if(tkErr)throw new Error(tkErr.message);

    await supabase.from('task_events').insert({org_id:orgId,task_id:task.id,event_type:'created',new_value:'pending',actor_id:uid});

    clearScan();setStage('upload');setExtracted(null);setMatched([]);setDuplicate(null);setPriceUpdates({});setNewItemEC({});setUploadedFile(null);
    setDeferMsg('Invoice saved to Tasks for later review');
    setTimeout(()=>setDeferMsg(''),4000);
    if(refreshBell)refreshBell();
  }catch(e){
    console.error('[InvoiceScanner] Defer error:',e);
    setError(e.message||'Failed to save for later');
  }
}

async function replaceExistingInvoice(){
  if(!duplicateOnConfirm)return;
  const ex=duplicateOnConfirm;
  // Snapshot old data before deleting so it's recoverable
  const[{data:oldPurchases},{data:oldLines},{data:oldFees}]=await Promise.all([
    supabase.from('purchases').select('*').eq('invoice_id',ex.id),
    supabase.from('invoice_line_items').select('*').eq('invoice_id',ex.id),
    supabase.from('categorized_expenses').select('*').eq('source_invoice_id',ex.id).eq('is_superseded',false),
  ]);
  const snapshot={replaced_at:new Date().toISOString(),purchases:oldPurchases||[],line_items:oldLines||[],expenses:oldFees||[]};
  await supabase.from('invoices').update({superseded_snapshot:snapshot}).eq('id',ex.id);
  // Delete old invoice data
  await supabase.from('purchases').delete().eq('invoice_id',ex.id);
  await supabase.from('invoice_line_items').delete().eq('invoice_id',ex.id);
  if(oldFees?.length){
    await supabase.from('categorized_expenses').delete().eq('source_invoice_id',ex.id);
    if(setOtherExp){const feeIds=new Set(oldFees.map(f=>f.id));setOtherExp(p=>p.filter(e=>!feeIds.has(e.id)));}
  }
  // Mark old invoice as superseded
  await supabase.from('invoices').update({superseded_by:'replaced'}).eq('id',ex.id);
  setDuplicateOnConfirm(null);
  // Re-run confirm — skip dup check since we just superseded the old invoice
  confirmInvoice({skipDupCheck:true});
}

function reset(){clearScan();setStage('upload');setExtracted(null);setMatched([]);setDuplicate(null);setDuplicateOnConfirm(null);setError('');setPriceUpdates({});setNewItemEC({});setUploadedFile(null);setDeferMsg('');setInvoicePages([]);}
useEffect(()=>{if(stage==='done'&&queue.length===0){const timer=setTimeout(reset,1500);return()=>clearTimeout(timer);}},[stage,queue.length]);

return(<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:18,marginBottom:16}}>
<div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Invoice Scanner</div>
<div style={{fontSize:11,color:t.td,marginBottom:14}}>Snap a photo or upload a PDF to auto-extract invoice data.</div>

{/* Defer success message */}
{deferMsg&&<div style={{background:"rgba(45,184,122,0.08)",border:`1px solid ${t.gn}`,borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:t.gn,fontWeight:500}}>{deferMsg}</div>}

{/* UPLOAD STAGE */}
{stage==='upload'&&<div>
<input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>{handleFile(e.target.files?.[0]);e.target.value='';}}/>
<input ref={fileRef} type="file" accept="image/*,.heic,application/pdf" multiple style={{display:"none"}} onChange={e=>{const files=e.target.files;if(files?.length>1)handleBulkFiles(files);else if(files?.[0])handleFile(files[0]);e.target.value='';}}/>
<div
  onDragEnter={e=>{e.preventDefault();e.stopPropagation();dragCounter.current++;setDragging(true);}}
  onDragLeave={e=>{e.preventDefault();e.stopPropagation();dragCounter.current--;if(dragCounter.current===0)setDragging(false);}}
  onDragOver={e=>{e.preventDefault();e.stopPropagation();}}
  onDrop={e=>{e.preventDefault();e.stopPropagation();setDragging(false);dragCounter.current=0;const files=e.dataTransfer?.files;if(files?.length>1)handleBulkFiles(files);else if(files?.[0])handleFile(files[0]);}}
  onClick={()=>fileRef.current?.click()}
  style={{
    border:`2px dashed ${dragging?t.bl:t.bd}`,
    borderRadius:12,
    padding:"40px 20px",
    textAlign:"center",
    cursor:"pointer",
    background:dragging?"rgba(59,130,246,0.06)":"transparent",
    transition:"all 0.15s ease",
  }}
>
  <div style={{fontSize:32,marginBottom:10,opacity:dragging?1:0.5}}>{dragging?"📥":"📄"}</div>
  <div style={{fontSize:14,fontWeight:600,color:dragging?t.bl:t.tx,marginBottom:4}}>
    {dragging?"Drop invoice here":"Drag invoice here or tap to upload"}
  </div>
  <div style={{fontSize:11,color:t.td,marginBottom:14}}>PDF, JPG, PNG, or HEIC — max 20MB</div>
  <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
    <button onClick={()=>cameraRef.current?.click()} style={{...sb,background:t.bl,display:"flex",alignItems:"center",gap:6,padding:"10px 18px",minHeight:44}}>
      <span style={{fontSize:15}}>📷</span> Camera
    </button>
    <button onClick={()=>fileRef.current?.click()} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,display:"flex",alignItems:"center",gap:6,padding:"10px 18px",minHeight:44}}>
      <span style={{fontSize:15}}>📁</span> Browse Files
    </button>
  </div>
</div>
{error&&<div style={{fontSize:11,color:t.ac,marginTop:10}}>{error}</div>}
</div>}

{/* PROCESSING STAGES */}
{(stage==='uploading'||stage==='reading')&&<ScanProgress stage={stage} t={t}/>}

{/* BULK SCANNING STAGE */}
{stage==='scanning'&&scanProgress&&<div style={{textAlign:"center",padding:"40px 0"}}>
<div style={{fontSize:24,marginBottom:12}}>🔍</div>
<div style={{fontSize:14,fontWeight:600,marginBottom:6}}>{scanProgress.current} of {scanProgress.total} scanned</div>
<div style={{fontSize:11,color:t.td,marginBottom:12}}>{scanProgress.current<scanProgress.total?'Processing up to 3 at a time...':'Finishing up...'}</div>
<div style={{width:200,height:4,background:t.bd,borderRadius:2,margin:'0 auto'}}>
<div style={{width:`${(scanProgress.current/scanProgress.total)*100}%`,height:'100%',background:t.bl,borderRadius:2,transition:'width 0.3s'}}/>
</div>
</div>}

{/* BULK QUEUE STAGE */}
{stage==='queue'&&<div>
{/* Queue header */}
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<div style={{fontSize:13,fontWeight:600}}>Invoice {queueIdx+1} of {queue.length} — {extracted?.vendor} {extracted?.invoice_number?`#${extracted.invoice_number}`:''}</div>
<button onClick={resetQueue} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10}}>Cancel All</button>
</div>

{/* Queue pills */}
<div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:14}}>
{queue.map((q,i)=>{
  const isCurrent=i===queueIdx;
  const bg=q.status==='confirmed'?'rgba(45,184,122,0.15)':q.status==='error'?'rgba(200,55,45,0.15)':q.status==='skipped'?'rgba(212,168,36,0.15)':isCurrent?'rgba(59,130,246,0.15)':t.sf;
  const color=q.status==='confirmed'?t.gn:q.status==='error'?t.ac:q.status==='skipped'?t.yl:isCurrent?t.bl:t.td;
  const icon=q.status==='confirmed'?'✓':q.status==='error'?'✗':q.status==='skipped'?'○':isCurrent?'►':'·';
  return <button key={q.id} onClick={()=>{if(q.status!=='error'){setQueueIdxSync(i);if(q.status==='confirmed'){setExtracted(q.extracted);setMatched(q.matched||[]);setPriceUpdates(q.priceUpdates||{});setNewItemEC(q.newItemEC||{});setUploadedFile(q.uploadedFile);setInvoicePages(q.invoicePages);setQueueLoading(false);}else{activateQueueItem(queueRef.current,i);}}}}
    style={{...sb,background:bg,color,border:isCurrent?`2px solid ${t.bl}`:`1px solid ${t.bd}`,fontSize:10,padding:'4px 8px',borderRadius:6,cursor:q.status!=='error'?'pointer':'default',opacity:q.status==='error'?0.6:1}}
    title={`${q.fileName}${q.error?' — '+q.error:''}`}>
    {icon} {i+1}
  </button>;
})}
</div>

{/* Duplicate warning for this queue item */}
{queue[queueIdx]?.duplicate&&<div style={{background:"rgba(212,168,36,0.08)",border:`1px solid ${t.yl}`,borderRadius:8,padding:12,marginBottom:12,fontSize:12}}>
<span style={{fontWeight:600,color:t.yl}}>Possible duplicate</span> — this vendor + invoice # already exists (${fmt$0(queue[queueIdx].duplicate.grand_total||0)}). Review before confirming.
</div>}

{/* Error state for failed queue item */}
{queue[queueIdx]?.status==='error'?<div style={{textAlign:'center',padding:'40px 20px'}}>
<div style={{fontSize:24,marginBottom:12,color:t.ac}}>✗</div>
<div style={{fontSize:14,fontWeight:600,color:t.ac,marginBottom:6}}>Scan Failed</div>
<div style={{fontSize:12,color:t.td,marginBottom:4}}>{queue[queueIdx]?.fileName}</div>
<div style={{fontSize:11,color:t.ac,marginBottom:16}}>{queue[queueIdx]?.error||'Unknown error'}</div>
<button onClick={skipQueueItem} style={{...sb,background:t.bl}}>Next</button>
</div>:queueLoading?<div style={{textAlign:'center',padding:'40px 0'}}>
<div style={{fontSize:24,marginBottom:12}}>🔍</div>
<div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Matching ingredients...</div>
<div style={{fontSize:11,color:t.td}}>Checking against current catalog</div>
</div>:<>

{/* Invoice preview */}
{invoicePages.length>0?<InvoiceOriginalViewer storagePath={invoicePages[0].storagePath} bucket="invoices" t={t}/>:previewUnavailable&&<div style={{padding:12,fontSize:11,color:t.td,fontStyle:'italic'}}>Preview unavailable</div>}

{duplicateOnConfirm&&<div style={{background:"rgba(200,55,45,0.08)",border:`1px solid ${t.ac}`,borderRadius:8,padding:16,marginBottom:14}}>
<div style={{fontSize:14,fontWeight:600,color:t.ac,marginBottom:6}}>Duplicate Invoice</div>
<div style={{fontSize:12,color:t.tx,marginBottom:8}}>An invoice for <strong>{extracted?.vendor}</strong>{duplicateOnConfirm.invoice_number?' #'+duplicateOnConfirm.invoice_number:''} already exists (${fmt$0(duplicateOnConfirm.grand_total||0)}).{duplicateOnConfirm._vendorDiffers&&<span style={{color:t.yl,fontWeight:600}}> Note: vendor names differ — existing is "{duplicateOnConfirm.vendor}"</span>}</div>
<div style={{display:'flex',gap:8}}>
<button onClick={replaceExistingInvoice} style={{...sb,background:t.ac,fontSize:12}}>Replace Existing</button>
<button onClick={()=>setDuplicateOnConfirm(null)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:12}}>Cancel</button>
</div>
</div>}

{extracted&&(()=>{
const isConfirmed=queue[queueIdx]?.status==='confirmed';
return<>
{isConfirmed&&<div style={{background:'rgba(45,184,122,0.08)',border:`1px solid ${t.gn}`,borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:t.gn,fontWeight:500}}>This invoice has been confirmed.</div>}
<InvoiceReview key={queue[queueIdx]?.id||queueIdx} extracted={extracted} matched={matched} setMatched={isConfirmed?()=>{}:setMatched} priceUpdates={priceUpdates} setPriceUpdates={isConfirmed?()=>{}:setPriceUpdates} newItemEC={newItemEC} setNewItemEC={isConfirmed?()=>{}:setNewItemEC} newItemCat={newItemCat} setNewItemCat={isConfirmed?()=>{}:setNewItemCat} suggestEC={suggestEC} onConfirm={isConfirmed?null:confirmAndAdvance} onCancel={isConfirmed?null:skipQueueItem} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} error={error} ings={ings} setIngs={setIngs} settings={settings} save={save} orgId={orgId} uid={uid} queueMode={isConfirmed?null:{position:queueIdx+1,total:queue.length,onSkip:skipQueueItem,confirming}}/>
</>;
})()}
</>}
</div>}

{/* BULK QUEUE DONE STAGE */}
{stage==='queue-done'&&<div style={{textAlign:'center',padding:'40px 20px'}}>
<div style={{fontSize:36,marginBottom:12}}>✓</div>
<div style={{fontSize:20,fontWeight:700,color:t.gn,marginBottom:16}}>Batch Complete</div>
{(()=>{
  const confirmed=queue.filter(q=>q.status==='confirmed');
  const skipped=queue.filter(q=>q.status==='skipped');
  const failed=queue.filter(q=>q.status==='error');
  const total=confirmed.reduce((s,q)=>s+(q.extracted?.grand_total||0),0);
  return<div style={{maxWidth:400,margin:'0 auto',textAlign:'left'}}>
    <div style={{fontSize:13,marginBottom:8}}><span style={{color:t.gn,fontWeight:600}}>{confirmed.length} confirmed</span> — ${fmt$0(total)}</div>
    {skipped.length>0&&<div style={{fontSize:13,marginBottom:8,color:t.yl}}>{skipped.length} skipped</div>}
    {failed.length>0&&<div style={{marginBottom:8}}>{failed.map(q=><div key={q.id} style={{fontSize:11,color:t.ac,marginBottom:4}}>{q.fileName}: {q.error}</div>)}</div>}
    {skipped.length>0&&<button onClick={()=>{const si2=queue.findIndex(q=>q.status==='skipped');setQueueIdxSync(si2);activateQueueItem(queue,si2);setStage('queue');}} style={{...sb,background:t.yl,marginTop:8}}>Review Skipped ({skipped.length})</button>}
  </div>;
})()}
<button onClick={resetQueue} style={{...sb,background:t.bl,marginTop:16}}>Done</button>
</div>}

{/* REVIEW STAGE */}
{stage==='review'&&extracted&&<div>
{/* Duplicate warning */}
{duplicate&&<div style={{background:"rgba(200,55,45,0.08)",border:`1px solid ${t.ac}`,borderRadius:8,padding:14,marginBottom:14}}>
<div style={{fontSize:13,fontWeight:600,color:t.ac,marginBottom:4}}>Duplicate Invoice</div>
<div style={{fontSize:12}}>This {extracted.vendor} invoice #{extracted.invoice_number} was already uploaded on {new Date(duplicate.created_at).toLocaleDateString()}. </div>
<div style={{display:"flex",gap:8,marginTop:10}}>
<button onClick={reset} style={{...sb,background:t.tm}}>Cancel</button>
</div>
</div>}

{!duplicate&&<>
{invoicePages.length>0&&<div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
<input ref={addPageRef} type="file" accept="image/*,.heic,application/pdf" style={{display:'none'}} onChange={e=>{addPageToReview(e.target.files?.[0]);e.target.value='';}}/>
<span style={{fontSize:10,color:t.td}}>{invoicePages.length} page{invoicePages.length!==1?'s':''}</span>
{invoicePages.length<10&&<button onClick={()=>addPageRef.current?.click()} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10}}>+ Add Page</button>}
</div>}
{invoicePages.length>0?<InvoiceOriginalViewer storagePath={invoicePages[0].storagePath} bucket="invoices" t={t}/>:previewUnavailable&&<div style={{padding:12,fontSize:11,color:t.td,fontStyle:'italic'}}>Preview unavailable</div>}
{duplicateOnConfirm&&<div style={{background:"rgba(200,55,45,0.08)",border:`1px solid ${t.ac}`,borderRadius:8,padding:16,marginBottom:14}}>
<div style={{fontSize:14,fontWeight:600,color:t.ac,marginBottom:6}}>Duplicate Invoice</div>
<div style={{fontSize:12,color:t.tx,marginBottom:8}}>An invoice for <strong>{extracted.vendor}</strong>{duplicateOnConfirm.invoice_number?' #'+duplicateOnConfirm.invoice_number:''} already exists (${fmt$0(duplicateOnConfirm.grand_total||0)}). This invoice cannot be saved until the duplicate is resolved.{duplicateOnConfirm._vendorDiffers&&<span style={{color:t.yl,fontWeight:600}}> Note: vendor names differ — existing is "{duplicateOnConfirm.vendor}"</span>}</div>
<div style={{display:'flex',gap:8}}>
<button onClick={replaceExistingInvoice} style={{...sb,background:t.ac,fontSize:12}}>Replace Existing Invoice</button>
<button onClick={()=>setDuplicateOnConfirm(null)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:12}}>Cancel</button>
</div>
</div>}
<InvoiceReview extracted={extracted} matched={matched} setMatched={setMatched} priceUpdates={priceUpdates} setPriceUpdates={setPriceUpdates} newItemEC={newItemEC} setNewItemEC={setNewItemEC} newItemCat={newItemCat} setNewItemCat={setNewItemCat} suggestEC={suggestEC} onConfirm={confirmInvoice} onCancel={reset} onDefer={deferToTasks} t={t} si={si} sb={sb} sh={sh} sd={sd} mn={mn} error={error} ings={ings} setIngs={setIngs} settings={settings} save={save} orgId={orgId} uid={uid}/>
</>}
</div>}

{/* DONE STAGE — auto-resets to upload after delay */}
{stage==='done'&&(()=>{
  const invDate=extracted?.invoice_date||new Date().toISOString().split('T')[0];
  const ws=settings?.weekStart||0;
  const today=parseLocalDate(salesDate||new Date().toISOString().split('T')[0]);
  const dow=today.getDay();const diff=(dow-ws+7)%7;
  const wkStart=new Date(today);wkStart.setDate(today.getDate()-diff);
  const wkEnd=new Date(wkStart);wkEnd.setDate(wkStart.getDate()+6);
  const inCurrentWeek=invDate>=wkStart.toISOString().split('T')[0]&&invDate<=wkEnd.toISOString().split('T')[0];
  return(<div style={{textAlign:"center",padding:"40px 20px"}}>
  <div style={{fontSize:36,marginBottom:12}}>✓</div>
  <div style={{fontSize:20,fontWeight:700,color:t.gn,marginBottom:10}}>Invoice Saved</div>
  <div style={{fontSize:15,color:t.tx,marginBottom:6,fontWeight:500}}>{extracted?.vendor} — <span style={{fontFamily:mn}}>${fmt$(extracted?.grand_total||0)}</span></div>
  <div style={{fontSize:13,color:t.tm,marginBottom:8}}>Invoice date: {invDate}</div>
  {!inCurrentWeek&&<div style={{fontSize:15,color:t.yl,fontWeight:600,lineHeight:1.5,margin:"16px auto",maxWidth:360,padding:"14px 18px",background:"rgba(212,168,36,0.08)",border:`1px solid rgba(212,168,36,0.3)`,borderRadius:8}}>This invoice is outside the current week. Use the ← → arrows below to navigate to the week of {invDate}.</div>}
  <div style={{display:'flex',gap:12,justifyContent:'center',marginTop:16}}>{onClose&&<button onClick={()=>{reset();onClose();}} style={{...sb,background:t.tm,padding:"10px 24px",fontSize:13}}>Close Scanner</button>}<div style={{fontSize:11,color:t.td,paddingTop:8}}>Returning to scanner...</div></div>
  </div>);
})()}
</div>);
}
